import { useQuery } from '@tanstack/react-query';
import { getAllTenantsOptions, type TenantRead } from '@harmonyso/tenants-sdk';
import { useAllCellTenants } from '@/hooks/use-cell-tenants';

/**
 * Fetch all tenants and cache them.
 * Used for enriching conversations with tenant names.
 */
export function useTenants() {
  return useQuery({
    ...getAllTenantsOptions(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}

/**
 * Tenants from every cell, for pickers of features whose requests go to the
 * tenant's own cell. `data`, `isLoading` and `isError` mean what they do for
 * `useTenants`; `data` stays undefined until every cell has answered, so a
 * tenant from a slower cell isn't missing. `failures` lists the cells whose
 * tenants couldn't be listed.
 */
export function useAllTenants() {
  const { tenants, loadingCells, allFailed, failures } = useAllCellTenants({
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  const isLoading = loadingCells.length > 0;
  return {
    data: isLoading || allFailed ? undefined : tenants,
    isLoading,
    isError: allFailed,
    failures,
  };
}

/**
 * Create a tenant ID to name mapping for quick lookups.
 */
export function useTenantNameMap() {
  const { data: tenants } = useTenants();

  const tenantMap = new Map<string, string>();
  if (tenants) {
    for (const tenant of tenants) {
      tenantMap.set(tenant.id, tenant.company);
    }
  }

  return tenantMap;
}

/**
 * Get tenant name by ID from cached data.
 */
export function getTenantName(tenants: TenantRead[] | undefined, tenantId: string): string {
  if (!tenants) return tenantId.slice(0, 8);
  const tenant = tenants.find(t => t.id === tenantId);
  return tenant?.company || tenantId.slice(0, 8);
}
